import React, { useState, useEffect } from 'react';
import { 
  Bot, Power, Eye, EyeOff, Settings, AlertTriangle, Activity, 
  TrendingUp, Users, DollarSign, Clock, Shield, Zap, Volume2,
  Mic, Search, RefreshCw, ChevronDown, ChevronUp, BarChart3
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const AIControlPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [creditFeatures, setCreditFeatures] = useState(null);
  const [showUsageDetails, setShowUsageDetails] = useState(false);
  const [showCreditList, setShowCreditList] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [settingsRes, usageRes, featuresRes] = await Promise.all([
        api.get('/admin/ai-control/settings'),
        api.get('/admin/ai-control/usage-stats?days=30'),
        api.get('/admin/ai-control/credit-burning-features')
      ]);
      setSettings(settingsRes.data);
      setUsageStats(usageRes.data);
      setCreditFeatures(featuresRes.data);
    } catch (error) {
      console.error('Failed to fetch AI settings:', error);
      toast.error('Failed to load AI control settings');
    } finally {
      setLoading(false);
    }
  };

  const updateNaradSettings = async (newSettings) => {
    setSaving(true);
    try {
      await api.put('/admin/ai-control/digital-narad', newSettings);
      setSettings(prev => ({ ...prev, digital_narad: newSettings }));
      toast.success('Digital Narad settings updated!');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSamadhanSettings = async (newSettings) => {
    setSaving(true);
    try {
      await api.put('/admin/ai-control/samadhan', newSettings);
      setSettings(prev => ({ ...prev, samadhan: newSettings }));
      toast.success('Samadhan settings updated!');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const updateGlobalSettings = async (newSettings) => {
    setSaving(true);
    try {
      await api.put('/admin/ai-control/global', newSettings);
      setSettings(prev => ({ ...prev, global: newSettings }));
      toast.success('Global AI settings updated!');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const emergencyShutdown = async (enable) => {
    if (enable && !window.confirm('⚠️ This will STOP all AI features immediately! Continue?')) {
      return;
    }
    
    try {
      await api.post(`/admin/ai-control/emergency-shutdown?enable=${enable}`);
      toast.success(enable ? '🛑 Emergency shutdown ENABLED' : '✅ Emergency shutdown DISABLED');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update emergency shutdown');
    }
  };

  const formatCurrency = (usd) => {
    const inr = usd * 83; // Approximate USD to INR
    return `$${usd.toFixed(4)} (≈₹${inr.toFixed(2)})`;
  };

  if (loading) {
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 bg-white/10 rounded w-1/3 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-48 bg-white/10 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  const narad = settings?.digital_narad || {};
  const samadhan = settings?.samadhan || {};
  const global = settings?.global || {};

  return (
    <div className="p-6 space-y-6" data-testid="ai-control-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/20 to-blue-500/20">
            <Bot className="w-8 h-8 text-purple-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">AI Control Panel</h1>
            <p className="text-muted-foreground">Manage AI features & monitor credit usage</p>
          </div>
        </div>
        
        <button
          onClick={fetchAllData}
          className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-all"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Emergency Controls */}
      <div className={`p-4 rounded-xl border-2 ${global.emergency_shutdown ? 'bg-red-500/10 border-red-500' : 'bg-green-500/10 border-green-500/30'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className={`w-6 h-6 ${global.emergency_shutdown ? 'text-red-500' : 'text-green-500'}`} />
            <div>
              <p className="font-bold">Emergency Shutdown</p>
              <p className="text-sm text-muted-foreground">
                {global.emergency_shutdown ? 'ALL AI features are DISABLED' : 'AI features are running normally'}
              </p>
            </div>
          </div>
          
          <button
            onClick={() => emergencyShutdown(!global.emergency_shutdown)}
            className={`px-4 py-2 rounded-xl font-bold transition-all ${
              global.emergency_shutdown 
                ? 'bg-green-500 hover:bg-green-600 text-white' 
                : 'bg-red-500 hover:bg-red-600 text-white'
            }`}
          >
            {global.emergency_shutdown ? 'Re-Enable AI' : 'EMERGENCY STOP'}
          </button>
        </div>
      </div>

      {/* Master Voice Controls - NEW */}
      <div className="glass-card p-6 border-2 border-orange-500/30">
        <div className="flex items-center gap-3 mb-4">
          <Volume2 className="w-6 h-6 text-orange-500" />
          <div>
            <h2 className="text-xl font-bold">Master Voice Controls</h2>
            <p className="text-sm text-muted-foreground">Disable all voice features to save ₹1.75/message</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Disable ALL Voice */}
          <button
            onClick={async () => {
              const disable = !global.all_tts_disabled || !global.all_stt_disabled;
              try {
                await api.post(`/admin/ai-control/toggle-all-voice?disable=${disable}`);
                toast.success(disable ? '🔇 All voice features DISABLED' : '🔊 All voice features ENABLED');
                fetchAllData();
              } catch (e) { toast.error('Failed to update'); }
            }}
            className={`p-4 rounded-xl border-2 transition-all ${
              global.all_tts_disabled && global.all_stt_disabled
                ? 'bg-red-500/20 border-red-500 text-red-400'
                : 'bg-green-500/20 border-green-500 text-green-400'
            }`}
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              {global.all_tts_disabled && global.all_stt_disabled ? <EyeOff className="w-6 h-6" /> : <Zap className="w-6 h-6" />}
              <span className="font-bold">ALL VOICE</span>
            </div>
            <p className="text-xs">
              {global.all_tts_disabled && global.all_stt_disabled ? 'DISABLED (Saving ₹1.75/msg)' : 'ENABLED'}
            </p>
            <p className="text-xs mt-1 opacity-70">Click to {global.all_tts_disabled && global.all_stt_disabled ? 'enable' : 'disable'}</p>
          </button>

          {/* TTS Only */}
          <button
            onClick={async () => {
              try {
                await api.post(`/admin/ai-control/toggle-all-tts?disable=${!global.all_tts_disabled}`);
                toast.success(global.all_tts_disabled ? '🔊 TTS ENABLED' : '🔇 TTS DISABLED');
                fetchAllData();
              } catch (e) { toast.error('Failed to update'); }
            }}
            className={`p-4 rounded-xl border-2 transition-all ${
              global.all_tts_disabled
                ? 'bg-red-500/20 border-red-500 text-red-400'
                : 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
            }`}
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <Volume2 className="w-6 h-6" />
              <span className="font-bold">TTS (Voice Out)</span>
            </div>
            <p className="text-xs">
              {global.all_tts_disabled ? 'DISABLED (Saving ₹1.25/msg)' : 'ENABLED'}
            </p>
            <p className="text-xs mt-1 opacity-70">Text-to-Speech</p>
          </button>

          {/* STT Only */}
          <button
            onClick={async () => {
              try {
                await api.post(`/admin/ai-control/toggle-all-stt?disable=${!global.all_stt_disabled}`);
                toast.success(global.all_stt_disabled ? '🎤 STT ENABLED' : '🔇 STT DISABLED');
                fetchAllData();
              } catch (e) { toast.error('Failed to update'); }
            }}
            className={`p-4 rounded-xl border-2 transition-all ${
              global.all_stt_disabled
                ? 'bg-red-500/20 border-red-500 text-red-400'
                : 'bg-pink-500/20 border-pink-500 text-pink-400'
            }`}
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <Mic className="w-6 h-6" />
              <span className="font-bold">STT (Voice In)</span>
            </div>
            <p className="text-xs">
              {global.all_stt_disabled ? 'DISABLED (Saving ₹0.50/msg)' : 'ENABLED'}
            </p>
            <p className="text-xs mt-1 opacity-70">Speech-to-Text</p>
          </button>
        </div>

        <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <p className="text-sm text-yellow-500">
            💡 <b>Tip:</b> Disabling voice features saves ₹1.75 per voice interaction. Text chat still works!
          </p>
        </div>
      </div>

      {/* Usage Stats Summary */}
      {usageStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="glass-card p-4 border border-blue-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-blue-500" />
              <span className="text-sm text-muted-foreground">Total Uses (30d)</span>
            </div>
            <p className="text-2xl font-bold">{usageStats.total_uses?.toLocaleString() || 0}</p>
          </div>
          
          <div className="glass-card p-4 border border-yellow-500/30">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-yellow-500" />
              <span className="text-sm text-muted-foreground">Est. Cost (30d)</span>
            </div>
            <p className="text-2xl font-bold text-yellow-500">
              {formatCurrency(usageStats.total_estimated_cost_usd || 0)}
            </p>
          </div>
          
          <div className="glass-card p-4 border border-green-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-green-500" />
              <span className="text-sm text-muted-foreground">Top Users</span>
            </div>
            <p className="text-2xl font-bold">{usageStats.top_users?.length || 0}</p>
          </div>
          
          <div className="glass-card p-4 border border-purple-500/30">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-5 h-5 text-purple-500" />
              <span className="text-sm text-muted-foreground">Features Used</span>
            </div>
            <p className="text-2xl font-bold">{usageStats.by_feature?.length || 0}</p>
          </div>
        </div>
      )}

      {/* Digital Narad Controls */}
      <div className="glass-card p-6 border border-purple-500/30">
        <div className="flex items-center gap-3 mb-6">
          <Bot className="w-6 h-6 text-purple-500" />
          <h2 className="text-xl font-bold">Digital Narad Controls</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Enable/Disable Toggle */}
          <div className="p-4 rounded-xl bg-white/5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Power className="w-5 h-5 text-green-500" />
                <span className="font-medium">Feature Enabled</span>
              </div>
              <button
                onClick={() => updateNaradSettings({ ...narad, enabled: !narad.enabled })}
                disabled={saving}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  narad.enabled ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                }`}
              >
                {narad.enabled ? 'ON' : 'OFF'}
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              When OFF, users cannot chat with Digital Narad
            </p>
          </div>

          {/* Visibility Toggle */}
          <div className="p-4 rounded-xl bg-white/5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {narad.visible ? <Eye className="w-5 h-5 text-blue-500" /> : <EyeOff className="w-5 h-5 text-gray-500" />}
                <span className="font-medium">Show in UI</span>
              </div>
              <button
                onClick={() => updateNaradSettings({ ...narad, visible: !narad.visible })}
                disabled={saving}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  narad.visible ? 'bg-blue-500 text-white' : 'bg-gray-500 text-white'
                }`}
              >
                {narad.visible ? 'VISIBLE' : 'HIDDEN'}
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              When HIDDEN, Digital Narad won't appear on the website
            </p>
          </div>

          {/* Free User Limit */}
          <div className="p-4 rounded-xl bg-white/5">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-yellow-500" />
              <span className="font-medium">Free User Daily Limit</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={narad.free_daily_limit || 10}
                onChange={(e) => setSettings(prev => ({
                  ...prev, 
                  digital_narad: { ...narad, free_daily_limit: parseInt(e.target.value) }
                }))}
                className="w-24 px-3 py-2 rounded-lg bg-white/10 border border-white/20"
                min="0"
                max="100"
              />
              <span className="text-muted-foreground">messages/day</span>
              <button
                onClick={() => updateNaradSettings({ ...narad, free_daily_limit: narad.free_daily_limit })}
                disabled={saving}
                className="px-3 py-2 bg-primary text-white rounded-lg"
              >
                Save
              </button>
            </div>
          </div>

          {/* Activated User Limit */}
          <div className="p-4 rounded-xl bg-white/5">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-green-500" />
              <span className="font-medium">Activated User Daily Limit</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={narad.activated_daily_limit || 50}
                onChange={(e) => setSettings(prev => ({
                  ...prev, 
                  digital_narad: { ...narad, activated_daily_limit: parseInt(e.target.value) }
                }))}
                className="w-24 px-3 py-2 rounded-lg bg-white/10 border border-white/20"
                min="0"
                max="500"
              />
              <span className="text-muted-foreground">messages/day</span>
              <button
                onClick={() => updateNaradSettings({ ...narad, activated_daily_limit: narad.activated_daily_limit })}
                disabled={saving}
                className="px-3 py-2 bg-primary text-white rounded-lg"
              >
                Save
              </button>
            </div>
          </div>

          {/* Voice Features */}
          <div className="p-4 rounded-xl bg-white/5">
            <div className="flex items-center gap-2 mb-4">
              <Volume2 className="w-5 h-5 text-cyan-500" />
              <span className="font-medium">Text-to-Speech (TTS)</span>
            </div>
            <button
              onClick={() => updateNaradSettings({ ...narad, tts_enabled: !narad.tts_enabled })}
              disabled={saving}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                narad.tts_enabled ? 'bg-cyan-500 text-white' : 'bg-gray-500 text-white'
              }`}
            >
              {narad.tts_enabled ? 'ENABLED' : 'DISABLED'}
            </button>
            <p className="text-xs text-muted-foreground mt-2">Voice output costs ~₹1.25/message</p>
          </div>

          <div className="p-4 rounded-xl bg-white/5">
            <div className="flex items-center gap-2 mb-4">
              <Mic className="w-5 h-5 text-pink-500" />
              <span className="font-medium">Speech-to-Text (STT)</span>
            </div>
            <button
              onClick={() => updateNaradSettings({ ...narad, stt_enabled: !narad.stt_enabled })}
              disabled={saving}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                narad.stt_enabled ? 'bg-pink-500 text-white' : 'bg-gray-500 text-white'
              }`}
            >
              {narad.stt_enabled ? 'ENABLED' : 'DISABLED'}
            </button>
            <p className="text-xs text-muted-foreground mt-2">Voice input costs ~₹0.50/message</p>
          </div>
        </div>
      </div>

      {/* Avatar Visibility & Samadhan Controls */}
      <div className="glass-card p-6 border border-cyan-500/30">
        <div className="flex items-center gap-3 mb-6">
          <Eye className="w-6 h-6 text-cyan-500" />
          <div>
            <h2 className="text-xl font-bold">Avatar Visibility Controls</h2>
            <p className="text-sm text-muted-foreground">Show/Hide floating avatars across all pages</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Digital Narad Avatar */}
          <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Bot className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="font-bold">Digital Narad Avatar</p>
                  <p className="text-xs text-muted-foreground">AI chatbot floating icon</p>
                </div>
              </div>
              <button
                onClick={() => updateNaradSettings({ ...narad, visible: !narad.visible })}
                disabled={saving}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  narad.visible ? 'bg-purple-500 text-white' : 'bg-gray-600 text-white'
                }`}
              >
                {narad.visible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            </div>
            <p className={`text-sm ${narad.visible ? 'text-green-400' : 'text-red-400'}`}>
              {narad.visible ? '✓ Visible on all pages' : '✗ Hidden from users'}
            </p>
          </div>

          {/* Samadhan Avatar */}
          <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/30">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center">
                  <span className="text-white font-bold text-lg">स</span>
                </div>
                <div>
                  <p className="font-bold">Samadhan Avatar</p>
                  <p className="text-xs text-muted-foreground">Consultation service icon</p>
                </div>
              </div>
              <button
                onClick={() => updateSamadhanSettings({ ...samadhan, avatar_visible: !samadhan.avatar_visible })}
                disabled={saving}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  samadhan.avatar_visible !== false ? 'bg-orange-500 text-white' : 'bg-gray-600 text-white'
                }`}
              >
                {samadhan.avatar_visible !== false ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            </div>
            <p className={`text-sm ${samadhan.avatar_visible !== false ? 'text-green-400' : 'text-red-400'}`}>
              {samadhan.avatar_visible !== false ? '✓ Visible on all pages' : '✗ Hidden from users'}
            </p>
          </div>
        </div>

        {/* Samadhan Feature Controls */}
        <div className="mt-6 pt-6 border-t border-white/10">
          <h3 className="font-bold mb-4">Samadhan Service Controls</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-white/5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Power className="w-5 h-5 text-green-500" />
                  <span className="font-medium">Service Enabled</span>
                </div>
                <button
                  onClick={() => updateSamadhanSettings({ ...samadhan, enabled: !samadhan.enabled })}
                  disabled={saving}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                    samadhan.enabled !== false ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                  }`}
                >
                  {samadhan.enabled !== false ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>
            
            <div className="p-4 rounded-xl bg-white/5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Volume2 className="w-5 h-5 text-blue-500" />
                  <span className="font-medium">Voice Chat</span>
                </div>
                <button
                  onClick={() => updateSamadhanSettings({ ...samadhan, voice_enabled: !samadhan.voice_enabled })}
                  disabled={saving}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                    samadhan.voice_enabled !== false ? 'bg-blue-500 text-white' : 'bg-gray-500 text-white'
                  }`}
                >
                  {samadhan.voice_enabled !== false ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>
            
            <div className="p-4 rounded-xl bg-white/5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-purple-500" />
                  <span className="font-medium">AI Reports</span>
                </div>
                <button
                  onClick={() => updateSamadhanSettings({ ...samadhan, reports_enabled: !samadhan.reports_enabled })}
                  disabled={saving}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                    samadhan.reports_enabled !== false ? 'bg-purple-500 text-white' : 'bg-gray-500 text-white'
                  }`}
                >
                  {samadhan.reports_enabled !== false ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Credit-Burning Features List */}
      <div className="glass-card p-6 border border-yellow-500/30">
        <button
          onClick={() => setShowCreditList(!showCreditList)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <DollarSign className="w-6 h-6 text-yellow-500" />
            <h2 className="text-xl font-bold">Credit-Burning Features List</h2>
          </div>
          {showCreditList ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>

        {showCreditList && creditFeatures && (
          <div className="mt-6 space-y-4">
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
              <p className="font-bold text-yellow-500">⚠️ WARNING</p>
              <p className="text-sm">{creditFeatures.warning}</p>
            </div>

            {Object.entries(creditFeatures.features_by_category || {}).map(([category, features]) => (
              <div key={category}>
                <h3 className="font-bold capitalize mb-2">{category} Features</h3>
                <div className="space-y-2">
                  {features.map(feature => (
                    <div key={feature.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="font-medium">{feature.name}</p>
                        <p className="text-xs text-muted-foreground">{feature.model}</p>
                      </div>
                      <span className="text-yellow-500 font-mono">
                        ~₹{(feature.avg_cost_per_use_usd * 83).toFixed(2)}/use
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="p-4 bg-white/5 rounded-xl">
              <p className="font-bold mb-2">Recommendations:</p>
              <ul className="space-y-1 text-sm">
                {creditFeatures.recommendations?.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-green-500">✓</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Usage Details */}
      <div className="glass-card p-6">
        <button
          onClick={() => setShowUsageDetails(!showUsageDetails)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <Activity className="w-6 h-6 text-blue-500" />
            <h2 className="text-xl font-bold">Usage Details (Last 30 Days)</h2>
          </div>
          {showUsageDetails ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>

        {showUsageDetails && usageStats && (
          <div className="mt-6 space-y-6">
            {/* By Feature */}
            <div>
              <h3 className="font-bold mb-3">Usage by Feature</h3>
              <div className="space-y-2">
                {usageStats.by_feature?.map(feature => (
                  <div key={feature.feature_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div>
                      <p className="font-medium">{feature.feature_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {feature.unique_users} users • {feature.model}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{feature.total_uses} uses</p>
                      <p className="text-xs text-yellow-500">{formatCurrency(feature.estimated_cost)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Users */}
            <div>
              <h3 className="font-bold mb-3">Top AI Users</h3>
              <div className="space-y-2">
                {usageStats.top_users?.map((user, i) => (
                  <div key={user._id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold">
                        {i + 1}
                      </span>
                      <div>
                        <p className="font-medium">{user.user_info?.name || user._id}</p>
                        <p className="text-xs text-muted-foreground">@{user.user_info?.username}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{user.total_uses} uses</p>
                      <p className="text-xs text-yellow-500">{formatCurrency(user.total_cost)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIControlPanel;
